import React from 'react';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
} from 'react-router-dom';
import Modal from './components/Modal/Modal';
import Dashboard from './scenes/Dashboard/Dashboard';
import HmdPage from './scenes/HmdPage/HmdPage';
import CsrmPage from './scenes/CsrmPage/CsrmPage';
import CsrmChartsPage from './scenes/CsrmPage/CsrmChartsPage';
import DownloadPage from './scenes/DownloadPage/DownloadPage';
import QueryPage from './scenes/QueryPage/QueryPage';
import DevDownloadPage from './scenes/DevDownloadPage/DevDownloadPage';
import UploadPage from './scenes/UploadPage/UploadPage';
import UpdatePage from './scenes/UpdatePage/UpdatePage';
import HardwarePage from './scenes/HardwarePage/HardwarePage';
import ConvertFilesPage from './scenes/ConvertFilesPage/ConvertFilesPage';
import IotDownloadPage from './scenes/IotDownloadPage/IotDownloadPage';
import useModal from './helpers/useModal';
import Header from './components/Header/Header';
import Subheader from './components/Header/Subheader';
import Footer from './components/Footer/Footer';
import ProcessToken from './scenes/Token/ProcessToken';
import LoginPage from './scenes/Login/Login';
import { DataCollectionProvider } from './helpers/dataCollectionContext';
import { DashboardPreferencesProvider } from './contexts/DashboardPreferencesContext';
import { getStoredRoles } from './helpers/auth';
import {
  getAllowedRoutes, getDefaultRoute, canAccessRoute, getAllowedEnvironments, ROUTES,
} from './helpers/accessControl';

import './App.css';

const App = (props) => {
  const {
    isShowing,
    toggle,
    innerHTML,
    modalTitle,
  } = useModal();
  const { webSocketService, thermalWebsocketService } = props || {};
  const userRoles = getStoredRoles();
  const allowedRoutes = getAllowedRoutes(userRoles);
  const allowedEnvironments = getAllowedEnvironments(allowedRoutes, userRoles);
  const defaultRoute = getDefaultRoute(allowedRoutes);
  let auth = null;
  try {
    auth = sessionStorage.getItem('isAuthenticated');
  } catch {
    // do nothing
  }
  const { pathname } = window.location;
  const isLoginPath = /\/login\/?$/.test(pathname);
  const isAuthPath = /\/auth\/?$/.test(pathname);

  if (!isAuthPath && !isLoginPath && auth !== 'authenticated') {
    window.location.href = '/login';
  }
  if (auth === 'authenticated' && !isAuthPath && !isLoginPath && !canAccessRoute(allowedRoutes, pathname)) {
    window.location.href = defaultRoute;
  }

  // Gate a route's element by access. The top-level `window.location` check only
  // runs on initial load, so this guards client-side navigations too, redirecting
  // to the user's default route with react-router instead of rendering the page.
  const guard = (route, element) => (
    canAccessRoute(allowedRoutes, route) ? element : <Redirect to={defaultRoute} />
  );

  return (
    <DataCollectionProvider>
      <DashboardPreferencesProvider allowedEnvironments={allowedEnvironments}>
        <Router>
          {auth === 'authenticated' && !isLoginPath ? (
            <header className="Header">
              <Header allowedRoutes={allowedRoutes} defaultRoute={defaultRoute} />
            </header>
          ) : null}
          <div className="Layout">
            <main className="Layout-main">
              {auth === 'authenticated' && !isLoginPath ? (
                <div className="Subheader">
                  <Subheader defaultRoute={defaultRoute} allowedEnvironments={allowedEnvironments} allowedRoutes={allowedRoutes} />
                </div>
              ) : null}
              <Switch>
                <Route path="/login" component={LoginPage} />
                <Route exact path="/auth" component={ProcessToken} />
                {auth === 'authenticated' ? (
                  <Route exact path="/">
                    <Redirect to={defaultRoute} />
                  </Route>
                ) : null}
                <Route
                  path="/dashboard"
                  render={() => (
                    <Dashboard
                      webSocketService={webSocketService}
                      thermalWebsocketService={thermalWebsocketService}
                    />
                  )}
                />
                <Route
                  path="/hmd"
                  render={() => guard(ROUTES.HMD, (
                    <HmdPage webSocketService={webSocketService} />
                  ))}
                />
                <Route path="/csrm/charts" render={() => guard(ROUTES.CSRM_CHARTS, <CsrmChartsPage />)} />
                <Route path="/csrm" render={() => guard(ROUTES.CSRM, <CsrmPage />)} />
                <Route path="/hardware" render={() => guard(ROUTES.HARDWARE, <HardwarePage />)} />
                <Route path="/convert-files" render={() => guard(ROUTES.CONVERT_FILES, <ConvertFilesPage />)} />
                <Route path="/iot-download" render={() => guard(ROUTES.IOT_DOWNLOAD, <IotDownloadPage />)} />
                <Route
                  path="/calendar"
                  render={() => guard(ROUTES.CALENDAR, (
                    <DownloadPage jwt={(webSocketService || {}).jwt} />
                  ))}
                />
                <Route
                  path="/query"
                  render={() => guard(ROUTES.QUERY, <QueryPage />)}
                />
                <Route
                  path="/dev-download"
                  render={() => guard(ROUTES.DEV_DOWNLOAD, <DevDownloadPage />)}
                />
                <Route path="/upload" render={() => guard(ROUTES.UPLOAD, <UploadPage />)} />
                <Route
                  path="/update"
                  render={() => guard(ROUTES.UPDATE, (
                    <UpdatePage ghToken={(webSocketService || {}).ghToken} />
                  ))}
                />
              </Switch>
              <Modal
                isShowing={isShowing}
                hide={toggle}
                innerComponent={innerHTML}
                title={modalTitle}
              />
            </main>
            <Footer />
          </div>
        </Router>
      </DashboardPreferencesProvider>
    </DataCollectionProvider>
  );
};

export default App;
